/**
 * EnrollPage — Floor device landing page reached via QR code scan.
 * URL: /enroll?token=<hex>
 *
 * Flow:
 *   1. Page loads, reads ?token from URL
 *   2. Calls /api/webauthn/register/options-from-token → gets challenge + employee name
 *   3. Calls startRegistration() — device prompts Face ID / Touch ID
 *   4. Calls /api/webauthn/register/verify-from-token → verifies + issues JWT
 *   5. Stores JWT in Redux + localStorage, navigates to dashboard
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Alert, Stack } from "@mui/material";
import { FingerprintOutlined, CheckCircleOutlined, ErrorOutline } from "@mui/icons-material";
import { startRegistration } from "@simplewebauthn/browser";
import jwt_decode from "jwt-decode";
import { useDispatch } from "react-redux";
import { setUserFromToken } from "../feature/userSlice";
import ApiCall from "../utils/apicall";
import { setAccessToken } from "../utils/authToken";
import Logo from "../components/logo/Logo";

const STATUS = { IDLE: "idle", LOADING: "loading", READY: "ready", ENROLLING: "enrolling", DONE: "done", ERROR: "error" };

export default function EnrollPage() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const token      = params.get("token");

  const [status,    setStatus]    = useState(STATUS.LOADING);
  const [enrollee,  setEnrollee]  = useState("");
  const [options,   setOptions]   = useState(null);
  const [userId,    setUserId]    = useState(null);
  const [message,   setMessage]   = useState("");

  // Step 1: Fetch registration options as soon as page loads
  const fetchOptions = useCallback(async () => {
    if (!token) { setStatus(STATUS.ERROR); setMessage("No enrollment token in URL."); return; }
    setStatus(STATUS.LOADING);
    try {
      const res = await ApiCall.post("/webauthn/register/options-from-token", { token });
      const data = res.data?.data;
      if (!data?.challenge) throw new Error("Invalid response from server");
      const { _userId, _token: _t, _enrolleeName, ...regOptions } = data;
      setOptions(regOptions);
      setUserId(_userId);
      setEnrollee(_enrolleeName || "");
      setStatus(STATUS.READY);
    } catch (err) {
      setStatus(STATUS.ERROR);
      setMessage(err?.response?.data?.message || err?.message || "Token invalid or expired. Ask your manager to generate a new one.");
    }
  }, [token]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  // Step 2: Trigger biometric registration
  const handleEnroll = async () => {
    setStatus(STATUS.ENROLLING);
    try {
      const credential = await startRegistration(options);

      const verifyRes = await ApiCall.post("/webauthn/register/verify-from-token", {
        token,
        credential,
        userId,
      });

      const { token: jwt } = verifyRes.data?.data ?? {};
      if (!jwt) throw new Error("Server did not return a session token");

      const decodedToken = jwt_decode(jwt);
      setAccessToken(jwt);
      dispatch(setUserFromToken({ token: jwt, decodedToken }));

      setStatus(STATUS.DONE);
      // Short delay so user sees the success state, then navigate to dashboard
      setTimeout(() => navigate("/", { replace: true }), 2000);
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        setStatus(STATUS.READY); // let them try again
        setMessage("Biometric was cancelled. Tap the button to try again.");
      } else {
        setStatus(STATUS.ERROR);
        setMessage(err?.response?.data?.message || err?.message || "Enrollment failed.");
      }
    }
  };

  const platformAvailable =
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Logo sx={{ width: 64, height: 64, mb: 2 }} />
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, textAlign: "center" }}>
        SFM Biometric Enrollment
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4, textAlign: "center" }}>
        Shop Floor Management — A3 Assurance
      </Typography>

      <Box sx={{ maxWidth: 400, width: "100%", textAlign: "center" }}>

        {/* Loading */}
        {status === STATUS.LOADING && (
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Verifying enrollment token…</Typography>
          </Stack>
        )}

        {/* Ready */}
        {(status === STATUS.READY || status === STATUS.ENROLLING) && (
          <Stack alignItems="center" spacing={3}>
            <FingerprintOutlined sx={{ fontSize: 72, color: "primary.main" }} />
            {enrollee && (
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Welcome, {enrollee}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Set up Face ID on this device to sign in with a glance — no password needed.
            </Typography>
            {message && <Alert severity="warning" sx={{ textAlign: "left" }}>{message}</Alert>}
            {!platformAvailable && (
              <Alert severity="error">
                This device doesn't support biometric authentication. Please use a supported device (iPhone, iPad, Mac with Touch ID, or a Windows device with Windows Hello).
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={status === STATUS.ENROLLING || !platformAvailable}
              startIcon={
                status === STATUS.ENROLLING
                  ? <CircularProgress size={18} color="inherit" />
                  : <FingerprintOutlined />
              }
              onClick={handleEnroll}
              sx={{ py: 1.5 }}
            >
              {status === STATUS.ENROLLING ? "Setting up Face ID…" : "Set Up Face ID"}
            </Button>
          </Stack>
        )}

        {/* Success */}
        {status === STATUS.DONE && (
          <Stack alignItems="center" spacing={2}>
            <CheckCircleOutlined sx={{ fontSize: 72, color: "success.main" }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              You're all set!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Face ID registered. Signing you in…
            </Typography>
            <CircularProgress size={24} />
          </Stack>
        )}

        {/* Error */}
        {status === STATUS.ERROR && (
          <Stack alignItems="center" spacing={2}>
            <ErrorOutline sx={{ fontSize: 72, color: "error.main" }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Enrollment Failed
            </Typography>
            <Alert severity="error" sx={{ textAlign: "left", width: "100%" }}>
              {message}
            </Alert>
            <Button variant="outlined" onClick={() => navigate("/login")}>
              Back to Sign In
            </Button>
          </Stack>
        )}

      </Box>
    </Box>
  );
}
